// Idle timeout hook — hareketsizlik süresine göre uyarı ve auto-logout tetikler
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseIdleTimeoutOptions {
  /** Toplam idle süresi (ms). Bu süre sonunda onTimeout çağrılır. */
  idleMs: number;
  /** Çıkışa kaç ms kala uyarı tetiklensin. */
  warnMs: number;
  /** Uyarı eşiğine ulaşıldığında çağrılır. */
  onWarn: () => void;
  /** Süre dolunca çağrılır. Genelde logout. */
  onTimeout: () => void;
  /**
   * Cross-tab senkron için BroadcastChannel adı. Aynı origin'deki diğer
   * sekmelerde aktivite olduğunda timer buradan reset alır.
   */
  channelName: string;
  /** Hook aktif mi (false ise tüm listener'lar kapalı). */
  enabled?: boolean;
}

interface UseIdleTimeoutReturn {
  /** Uyarı modalı açık mı. */
  warning: boolean;
  /** Uyarıdan bu yana geçen ms (geri sayım için `warnMs - elapsed`). */
  remainingMs: number;
  /** Timer'ı elle resetle (örn. "Devam Et" butonu). */
  reset: () => void;
}

// Aktiviteyi algılamak için dinlenen eventler
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
];

// Mouse/keyboard event'lerini throttle için minimum aralık (ms)
const THROTTLE_MS = 1000;

export function useIdleTimeout({
  idleMs,
  warnMs,
  onWarn,
  onTimeout,
  channelName,
  enabled = true,
}: UseIdleTimeoutOptions): UseIdleTimeoutReturn {
  const [warning, setWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(warnMs);

  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const channelRef = useRef<BroadcastChannel | null>(null);
  const warningRef = useRef(false);
  const timedOutRef = useRef(false);

  // Callback'leri ref'te tut — dependency chain'i kırarak stale closure'ı önle
  const onWarnRef = useRef(onWarn);
  const onTimeoutRef = useRef(onTimeout);
  onWarnRef.current = onWarn;
  onTimeoutRef.current = onTimeout;

  // State + ref birlikte güncellensin ki effect dependency'si warning'e
  // bağlı olmasın
  const setWarningBoth = useCallback((v: boolean) => {
    warningRef.current = v;
    setWarning(v);
  }, []);

  // Tüm timer'ları temizle
  const clearTimers = useCallback(() => {
    if (warnTimerRef.current) {
      clearTimeout(warnTimerRef.current);
      warnTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Timer'ları yeniden kur — callback'ler ref üzerinden okunduğu için
  // bu fonksiyonun identity'si sadece idleMs/warnMs değişince değişir
  const scheduleTimers = useCallback(() => {
    clearTimers();
    lastActivityRef.current = Date.now();
    timedOutRef.current = false;

    const warnDelay = Math.max(0, idleMs - warnMs);

    warnTimerRef.current = setTimeout(() => {
      setWarningBoth(true);
      setRemainingMs(warnMs);
      onWarnRef.current();

      // Geri sayım ticker'ı — süre dolunca da logout tetikler
      const warnStart = Date.now();
      countdownRef.current = setInterval(() => {
        const elapsed = Date.now() - warnStart;
        const remaining = Math.max(0, warnMs - elapsed);
        setRemainingMs(remaining);
        if (remaining <= 0 && !timedOutRef.current) {
          timedOutRef.current = true;
          clearTimers();
          onTimeoutRef.current();
        }
      }, 250);

      // Yedek logout timer (interval throttle durumunda)
      logoutTimerRef.current = setTimeout(() => {
        if (!timedOutRef.current) {
          timedOutRef.current = true;
          clearTimers();
          onTimeoutRef.current();
        }
      }, warnMs);
    }, warnDelay);
  }, [idleMs, warnMs, clearTimers, setWarningBoth]);

  // Dış reset (örn. "Devam Et" butonu) — uyarıyı kapatıp timer'ı sıfırlar
  const reset = useCallback(() => {
    setWarningBoth(false);
    scheduleTimers();
    // Diğer sekmelere de reset yay
    channelRef.current?.postMessage({ type: 'reset', t: Date.now() });
  }, [scheduleTimers, setWarningBoth]);

  // Aktivite handler — uyarı aktif değilse otomatik reset yapar
  useEffect(() => {
    if (!enabled) return;

    const onActivity = () => {
      const now = Date.now();
      // Throttle
      if (now - lastActivityRef.current < THROTTLE_MS) return;
      // Uyarı açıkken otomatik reset yok — kullanıcının butona basması lazım
      if (warningRef.current) return;

      lastActivityRef.current = now;
      scheduleTimers();
      channelRef.current?.postMessage({ type: 'activity', t: now });
    };

    ACTIVITY_EVENTS.forEach((ev) => {
      window.addEventListener(ev, onActivity, { passive: true });
    });

    // Cross-tab kanal
    try {
      channelRef.current = new BroadcastChannel(channelName);
      channelRef.current.onmessage = (msg) => {
        if (!msg?.data) return;
        if (msg.data.type === 'activity') {
          // Başka sekmede aktivite — bizde uyarı yoksa resetle
          if (!warningRef.current) {
            lastActivityRef.current = Date.now();
            scheduleTimers();
          }
        } else if (msg.data.type === 'reset') {
          // Başka sekmede elle reset — uyarı varsa kapat
          setWarningBoth(false);
          scheduleTimers();
        } else if (msg.data.type === 'logout') {
          // Başka sekmede logout oldu — bu sekmede de timer'ı durdur
          clearTimers();
        }
      };
    } catch {
      // BroadcastChannel desteklenmiyor — cross-tab sync olmadan devam
      channelRef.current = null;
    }

    // İlk mount'ta timer'ları kur
    scheduleTimers();

    // Sekme geri geldiğinde idle süresini kontrol et (throttle koruması)
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (timedOutRef.current) return;
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= idleMs) {
        timedOutRef.current = true;
        clearTimers();
        onTimeoutRef.current();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => {
        window.removeEventListener(ev, onActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibility);
      clearTimers();
      channelRef.current?.close();
      channelRef.current = null;
    };
  }, [enabled, channelName, idleMs, scheduleTimers, clearTimers, setWarningBoth]);

  return { warning, remainingMs, reset };
}
